// React hooks for feature flags
import { useState, useEffect, useCallback } from 'react';
import { featureFlagsService } from '../services/featureFlagsService.js';

/**
 * Hook to evaluate multiple feature flags at once
 * @param {string[]} flags - Array of flag names to evaluate
 * @returns {Object} Object with flag names as keys and boolean values, plus loading state
 */
export function useFeatureFlags(flags = []) {
  const [flagValues, setFlagValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const evaluateFlags = useCallback(async () => {
    if (flags.length === 0) {
      setFlagValues({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await featureFlagsService.evaluateFlags(flags);
      setFlagValues(result);
    } catch (err) {
      console.error('Failed to evaluate feature flags:', err);
      setError(err);
      // Set all flags to false on error
      setFlagValues(flags.reduce((acc, flag) => ({ ...acc, [flag]: false }), {}));
    } finally {
      setLoading(false);
    }
  }, [flags]);

  useEffect(() => {
    evaluateFlags();
  }, [evaluateFlags]);

  const refetch = useCallback(() => {
    featureFlagsService.clearCache();
    evaluateFlags();
  }, [evaluateFlags]);

  return {
    flags: flagValues,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook to check if a single feature flag is enabled
 * @param {string} flagName - Name of the flag to check
 * @returns {Object} Object with isEnabled boolean, loading state, and error
 */
export function useFeatureFlag(flagName) {
  const { flags, loading, error, refetch } = useFeatureFlags([flagName]);
  
  return {
    isEnabled: flags[flagName] || false,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook for admin feature flag management
 * @returns {Object} Object with admin functions and state
 */
export function useAdminFeatureFlags() {
  const [flags, setFlags] = useState([]);
  const [segments, setSegments] = useState([]);
  const [userOverrides, setUserOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [flagsData, segmentsData, overridesData] = await Promise.all([
        featureFlagsService.getAllFlags(),
        featureFlagsService.getSegments(),
        featureFlagsService.getUserOverrides(),
      ]);

      setFlags(flagsData);
      setSegments(segmentsData);
      setUserOverrides(overridesData);
    } catch (err) {
      console.error('Failed to load admin feature flags data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const createFlag = useCallback(async (flagData) => {
    try {
      const newFlag = await featureFlagsService.createFlag(flagData);
      setFlags(prev => [...prev, newFlag]);
      return newFlag;
    } catch (err) {
      console.error('Failed to create feature flag:', err);
      throw err;
    }
  }, []);

  const updateFlag = useCallback(async (flagId, flagData) => {
    try {
      const updatedFlag = await featureFlagsService.updateFlag(flagId, flagData);
      setFlags(prev => prev.map(flag => 
        flag.id === flagId ? updatedFlag : flag
      ));
      return updatedFlag;
    } catch (err) {
      console.error('Failed to update feature flag:', err);
      throw err;
    }
  }, []);

  const deleteFlag = useCallback(async (flagId) => {
    try {
      await featureFlagsService.deleteFlag(flagId);
      setFlags(prev => prev.filter(flag => flag.id !== flagId));
    } catch (err) {
      console.error('Failed to delete feature flag:', err);
      throw err;
    }
  }, []);

  const setUserOverride = useCallback(async (userId, flagId, enabled) => {
    try {
      const override = await featureFlagsService.setUserOverride(userId, flagId, enabled);
      setUserOverrides(prev => [...prev.filter(o => !(o.userId === userId && o.flagId === flagId)), override]);
      return override;
    } catch (err) {
      console.error('Failed to set user override:', err);
      throw err;
    }
  }, []);

  const refresh = useCallback(() => {
    featureFlagsService.clearCache();
    loadData();
  }, [loadData]);

  return {
    flags,
    segments,
    userOverrides,
    loading,
    error,
    createFlag,
    updateFlag,
    deleteFlag,
    setUserOverride,
    refresh,
  };
}

/**
 * Hook to get feature flag segments for admin UI
 */
export function useFeatureFlagSegments() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSegments = async () => {
      try {
        setLoading(true);
        setError(null);
        const segmentsData = await featureFlagsService.getSegments();
        setSegments(segmentsData);
      } catch (err) {
        console.error('Failed to load feature flag segments:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadSegments();
  }, []);

  return { segments, loading, error };
}
